import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import Tooltip from '../Tooltip';
import Button from '../Button';
import { I18n } from 'react-redux-i18n';

const KpiContainer = styled.div`
  width: 313px;
  min-width: 313px;
  height: 110px;
  border-radius: 4px 0 0 4px;
  background-color: ${(props) => props.theme.colors['terra-cotta']};
  color: white;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 24px;
  position: relative;
  z-index: 1;
  overflow: hidden;

  .title {
    font-size: 14px !important;
    font-family: ${(props) => props.theme.fonts.oswald};
    text-transform: uppercase;
    font-weight: 400 !important;
  }

  .subTitle {
    font-size: 12px !important;
    font-weight: 400 !important;
  }

  transition: min-width 2sec;

  @media (max-width: 1200px) {
    width: 100%;
    border-radius: 4px;
  }

  @media (max-width: 1350px) {
    min-width: calc(250px + 1vw) !important;
  }

  .dateText {
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
  }

  ::after {
    content: ' ';
    width: 100%;
    height: 90%;
    opacity: 0.5;
    border-radius: 1.7px;
    left: 15px;
    top: 5px;
    -webkit-filter: blur(13px);
    filter: blur(13px);
    background-color: rgba(211, 85, 54, 0.41);
    z-index: -1;
    position: absolute;
  }

  .containerNextButton {
    position: absolute;
    right: 5px;
    top: 9px;
  }

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    max-height: 120px;
  }
`;

const KpiValueFirstCircle = styled.div`
  border-radius: 50%;
  border: 10px solid rgba(224, 137, 114, 0.3);
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  margin-left: 10px;
  margin-right: 10px;
`;

const KpiValueContainer = styled.div`
  border: 10px solid rgba(234, 147, 124, 0.4);
  border-radius: 50%;
  height: 60px;
  width: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  .kpiValueText {
    font-size: 10px;
    font-weight: 400;
    font-family: ${(props) => props.theme.fonts.oswald};
    font-style: normal;
    font-stretch: normal;
    margin-top: 5px;
    letter-spacing: normal;
    text-align: center;
    text-transform: uppercase;
  }
`;

const KpiValue = styled.div<{ valueText: string | null }>`
  z-index: 10;
  font-size: 40px;
  margin-top: ${(props) => (props.valueText ? '20px' : '0px')};
  letter-spacing: -1.84px;
`;

const KpiTitle = styled.p`
  font-size: 12px;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  font-weight: 600;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
`;

const TooltipContainer = styled.div`
  position: absolute;
  right: 11px;
  bottom: 9px;
`;

const TooltipContentContainer = styled.p`
  max-width: 40vh;
  font-family: ${(props) => props.theme.fonts.sfcompact};
  font-size: 12px;
  line-height: 2;
  padding: 10px;
`;

const ChangeButton = styled(Button).attrs((props) => ({
  type: 'round',
  icon: 'loadingArrow',
  color: props.theme.colors['terra-cotta'],
  iconColor: props.theme.colors['white']
}))`
  width: 25px;
  height: 25px;

  span {
    width: 13px;
    height: 13px;
  }
`;

type KeyPerformanceIndicatorProps = {
  title: string;
  value?: number | string;
  handleNext?: (() => void) | null;
  valueText?: string | null;
  subTitle?: string | null;
  date?: string | null;
  dateText?: string | null;
  tooltip?: string | null;
};

const KeyPerformanceIndicator = ({
  title,
  value,
  handleNext = null,
  valueText = null,
  subTitle = null,
  date = null,
  dateText = null,
  tooltip = null
}: KeyPerformanceIndicatorProps) => {
  return (
    <KpiContainer>
      <KpiValueFirstCircle>
        <KpiValueContainer>
          <KpiValue valueText={valueText}>{value}</KpiValue>
          {valueText && <p className="kpiValueText">{valueText}</p>}
        </KpiValueContainer>
      </KpiValueFirstCircle>
      <div>
        <KpiTitle className="title">{title}</KpiTitle>
        {date && (
          <p className="dateText">
            {`${dateText ? dateText : I18n.t('sinceThe')} 
            ${moment(date).format('DD/MM/YYYY')}`}
          </p>
        )}
        {subTitle && <KpiTitle className="subTitle">{subTitle}</KpiTitle>}
      </div>
      {tooltip && (
        <TooltipContainer>
          {/* @ts-expect-error It does return the correct type */}
          <Tooltip
            iconColor="rgba(255,255,255,1)"
            placement="top"
            noCursor={null}
            tooltipContentStyles={null}
            content={
              <TooltipContentContainer>{tooltip}</TooltipContentContainer>
            }
          />
        </TooltipContainer>
      )}
      {handleNext && (
        <div className="containerNextButton">
          <ChangeButton onClick={() => handleNext()} />
        </div>
      )}
    </KpiContainer>
  );
};

export default KeyPerformanceIndicator;
